import { useState } from 'react';

/**
 * The selection a picker's single policy check runs for.
 *
 * In single-select that is the only selection, so every pick starts a new
 * check, as it always has. In multi-select the first selection is held until
 * the picker is emptied, so adding or removing items — the checked one
 * included — makes no new request. That is only sound while every selection
 * verifiably shares one org: the policy is the org's, so one check stands for
 * all of them. A selection with an unknown org, or two different orgs, cannot
 * vouch for the held result, so the check falls back to the first selection.
 */
export function usePolicyCheckItem<TItem>(
  items: readonly TItem[],
  isMultiSelect: boolean,
  getOrgId: (item: TItem) => string | null,
): TItem | null {
  const first = items[0] ?? null;
  const [held, setHeld] = useState<TItem | null>(first);
  const commonOrgId = items.length > 0 ? getOrgId(items[0]) : null;
  const canReuse =
    isMultiSelect &&
    held !== null &&
    first !== null &&
    commonOrgId !== null &&
    getOrgId(held) === commonOrgId &&
    items.every((item) => getOrgId(item) === commonOrgId);
  const next = canReuse ? held : first;
  if (next !== held) setHeld(next);
  return next;
}
